import {
  Actions,
  AmountCell,
  Badge,
  CountCell,
  Image,
  ProgressCell,
  ToggleActiveStatus,
  ToggleArchiveStatus,
  useCanShow,
} from "@";
import type { BankTableRow, Project } from "../types";
import type { ColumnDef } from "@tanstack/react-table";

interface TableActionsProps {
  row: Project;
  deleteHandler: (id: number) => Promise<void>;
  ToggleActive: (id: number, value: boolean) => Promise<void>;
  ToggleArchive: (id: number, value: boolean) => Promise<void>;
}

export const TableActions = ({
  row,
  deleteHandler,
  ToggleActive,
  ToggleArchive,
}: TableActionsProps) => {
  return (
    <Actions
      notEdit={!useCanShow("editProjects")}
      notDelete={!useCanShow("editProjects")}
      row={row}
      url="projects"
      delete={deleteHandler}
    >
      {useCanShow("editProjects") && (
        <>
          <ToggleActiveStatus row={row} click={ToggleActive} />
          <ToggleArchiveStatus row={row} click={ToggleArchive} />
        </>
      )}
    </Actions>
  );
};

// ─── Cell building blocks ─────────────────────────────────────────────────

const ImageCell = ({ src, alt }: { src?: string; alt?: string }) => (
  <span className="inline-flex h-10 w-16 items-center justify-center overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700">
    <Image height={48} width={64} src={src} alt={alt} />
  </span>
);

const TitleCell = ({ title }: { title: string }) => {
  const truncated = title?.length > 60 ? title.slice(0, 60) + "…" : title;
  return (
    <span
      title={title}
      className="font-semibold tracking-tight text-gray-900 dark:text-gray-100"
    >
      {truncated}
    </span>
  );
};

// Classifications — up to 2 visible chips, then a +N overflow pill.
const ClassificationsCell = ({ items }: { items?: string[] }) => {
  if (!items || items.length === 0) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  }
  const visible = items.slice(0, 2);
  const hidden = items.length - visible.length;
  return (
    <span
      title={items.join("، ")}
      className="flex flex-wrap items-center gap-2"
    >
      {visible.map((label) => (
        <span
          key={label}
          className="inline-flex max-w-[140px] items-center truncate rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-900 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40"
        >
          {label}
        </span>
      ))}
      {hidden > 0 && (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold tabular-nums text-gray-600 ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:ring-gray-600">
          +{hidden}
        </span>
      )}
    </span>
  );
};

const StatusCell = ({
  isActive,
  isArchived,
}: {
  isActive: boolean;
  isArchived: boolean;
}) => (
  <div className="flex flex-wrap gap-1.5 py-1">
    <Badge
      title={isActive ? "مفعل" : "غير مفعل"}
      color={isActive ? "green" : "red"}
    />
    {isArchived && <Badge title="مؤرشف" color="dark" />}
  </div>
);

// ─── Column definitions ───────────────────────────────────────────────────

export const tableColumns: ColumnDef<Project>[] = [
  {
    size: 80,
    id: "image",
    header: "",
    cell: ({ row }) => (
      <ImageCell src={row.original.imageFullUrl} alt={row.original.title} />
    ),
  },
  {
    id: "title",
    header: "العنوان",
    size: 200,
    cell: ({ row }) => <TitleCell title={row.original.title} />,
  },
  {
    id: "requiredAmount",
    header: "المبلغ المطلوب",
    cell: ({ row }) => <AmountCell value={row.original.requiredAmount} />,
  },
  {
    id: "totalAmount",
    header: "الإجمالي المُحصَّل",
    cell: ({ row }) => (
      <AmountCell value={row.original.totalAmount} tone="emerald" />
    ),
  },
  {
    id: "donationCount",
    size: 110,
    header: "عدد التبرعات",
    cell: ({ row }) => (
      <CountCell value={row.original.donationCount} label="متبرع" />
    ),
  },
  {
    id: "progressPercentage",
    size: 110,
    header: "نسبة الإنجاز",
    cell: ({ row }) => (
      <ProgressCell value={row.original.progressPercentage} tone="emerald" />
    ),
  },
  {
    size: 80,
    id: "classifications",
    header: "التصنيف",
    cell: ({ row }) => (
      <ClassificationsCell
        items={row.original.classifications as unknown as string[]}
      />
    ),
  },
  {
    id: "status",
    size: 80,
    header: "الحالة",
    cell: ({ row }) => (
      <StatusCell
        isActive={row.original.isActive}
        isArchived={row.original.isArchived}
      />
    ),
  },
];

interface BankColumnDef {
  name: string;
  header: string;
  cell?: (row: BankTableRow) => JSX.Element;
  selector?: (row: BankTableRow) => string;
}

export const banksColumns: BankColumnDef[] = [
  {
    name: "bank",
    header: "البنك",
    cell: ({ bankImageFullUrl, bankName }) => (
      <span className="flex items-center gap-2">
        <Image
          className="me-3 rounded-2xl object-contain"
          size={48}
          src={bankImageFullUrl}
        />
        {bankName}
      </span>
    ),
  },
  {
    name: "name",
    header: "اسم الحساب",
    selector: ({ name }) => name,
  },
  {
    name: "iban",
    header: "رقم iban",
    selector: ({ iban }) => iban,
  },
];
